import { CustomNavigationAndroidClient } from './custom-navigation-client-android';
import { IpcMessages } from '@et/electron';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MsalGuardConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { OAuthService } from 'angular-oauth2-oidc';
import { Capacitor } from '@capacitor/core';
import { environment } from '@et/environment';
import { platformType, RequiredMfaMethod } from '@et/typings';
import { CustomNavigationClient } from './custom-navigation-client';
import { isOnline } from '@et/utils';
import packageInfo from '@et/package';
import { ProfileV2ApiService } from '@et/api';
import { useBetterAuthForCurrentPlatform } from '@et/guards';
import {
  buildOidcAuthorizeResumeUrl,
  hasPendingOidcAuthorizeRequest,
  usesNativeMsalFlow,
} from '../../oauth/oidc-login.utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;

interface BetterAuthSignInResponse {
  token?: string;
  user?: { id?: string; email?: string };
  /** Set when the user has 2FA enabled — credentials verified, but no session yet. */
  twoFactorRedirect?: boolean;
}

@Component({
  selector: 'auth-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private profileV2Api = inject(ProfileV2ApiService);
  // Optional: the Better Auth (web) path can run without MSAL/OAuth providers.
  private authService = inject(MsalService, { optional: true });
  private msalGuardConfig = inject<MsalGuardConfiguration | null>(
    MSAL_GUARD_CONFIG,
    { optional: true },
  );
  private oauthService = inject(OAuthService, { optional: true });

  useBetterAuth = useBetterAuthForCurrentPlatform();

  /**
   * Env kill-switch: when true, skip per-user lookup and always use email OTP
   * as first factor (legacy passwordless mode).
   */
  emailOtpEnabled = environment.betterAuth?.emailOtpEnabled ?? false;

  /** Two-factor (2FA) state. Only reachable via the password path (§6). */
  twoFactorEnabled = environment.betterAuth?.twoFactorEnabled ?? false;
  twoFactorStep = false; // true = 2FA code-entry mode after credentials verified
  incorrect2fa = false; // wrong/expired 2FA code
  private twoFactorEmail = ''; // email carried into the 2FA step for resumeAfterSession
  /**
   * Gates the "Use an authenticator app instead" toggle. Independent of
   * `twoFactorEnabled` — TOTP is an additive factor on top of the existing OTP
   * 2FA step, not a replacement (docs/tasks/2026-08-18-totp-2fa-spa-implementation-plan.md).
   */
  totpEnabled = environment.betterAuth?.totpEnabled ?? false;
  /** true = verify against verify-totp instead of the default verify-otp. */
  useAuthenticatorApp = false;
  /** true = verify against verify-backup-code instead. Mutually exclusive with
   * useAuthenticatorApp; both reset to false when re-entering the 2FA step. */
  useBackupCode = false;
  /**
   * Effective Profile/company `requiredMfaMethod` from the anonymous sign-in-method
   * probe. Drives whether the 2FA step defaults to authenticator vs emailed/SMS OTP.
   */
  resolvedRequiredMfaMethod: RequiredMfaMethod = 'None';

  /** Email-first: false until Continue resolves sign-in-method (or env forces OTP). */
  emailLookupDone = false;
  resolvedFirstFactor: 'Password' | 'EmailOtp' | null = null;

  incorrectCred = false;
  profileLoadError = false;
  /** True when a guard (e.g. etAdminGuard) rejected an authenticated, authorized-looking
   * session — the credentials/session are fine, they just lack the required role. */
  accessDeniedError = false;
  /**
   * True after credentials/2FA succeed (or when a live session is being
   * forwarded off /auth/login) while OIDC + `/self` bootstrap finish. Keeps the
   * login form from flashing until home (or a profile_load error) takes over.
   */
  completingSignIn = false;
  form: UntypedFormGroup = this.createForm();
  returnUrl!: string;
  submitted = false;
  /** Password field visibility toggle (Better Auth credentials path). */
  showPassword = false;
  deviceIsNative = this.isNative();
  /**
   * iOS/Android + Better Auth: login runs entirely in the system browser (see
   * `escrowtab-oauth.config.ts`), so no credentials are collected in-app —
   * render a single "Continue to sign in" action instead of the form.
   */
  mobileNativeSystemBrowser =
    this.useBetterAuth && this.isMobileNativePlatform();
  isOnline = isOnline();
  version = packageInfo?.nativeVersion;
  devToolCount = 0;
  otpStep = false; // false = credentials mode, true = code-entry mode
  incorrectCode = false; // wrong/expired code on /sign-in/email-otp
  genericSendError = false; // send-otp transport failure; unknown user still returns 200

  /** Captured authorize query when the issuer redirected here mid-PKCE. */
  private pendingAuthorizeQuery: Params | null = null;

  ngOnInit() {
    const params = this.route.snapshot.queryParams;
    this.returnUrl = params['returnUrl'] || '/';
    if (this.useBetterAuth && hasPendingOidcAuthorizeRequest(params)) {
      this.pendingAuthorizeQuery = params;
    }
    const error = params['error'];
    if (
      error === 'profile_load' ||
      error === 'session_timeout' ||
      error === 'oidc_callback'
    ) {
      this.profileLoadError = true;
    }
    if (error === 'not_admin') {
      this.accessDeniedError = true;
    }
    // Clear credential-failure copy as soon as the user edits either field so
    // the alert doesn't linger over a corrected attempt.
    this.form.valueChanges.subscribe(() => {
      if (this.incorrectCred) {
        this.incorrectCred = false;
      }
    });
    // Already authenticated but landed on /auth/login — e.g. a guard bounced here
    // during a token-check race, or the user reopened the login route with a live
    // session. Honor the existing Better Auth session and forward to the requested
    // route instead of stranding them on the form. Skipped while an authorize
    // resume is pending or an error is being surfaced (those need the form).
    if (
      this.useBetterAuth &&
      !this.pendingAuthorizeQuery &&
      !error &&
      (this.oauthService?.hasValidAccessToken() ||
        this.oauthService?.hasValidIdToken())
    ) {
      this.completingSignIn = true;
      void this.router.navigateByUrl(this.returnUrl);
    }
  }

  /** True when the password field should be visible (after email-first lookup). */
  get showPasswordField(): boolean {
    return (
      this.useBetterAuth &&
      !this.emailOtpEnabled &&
      this.emailLookupDone &&
      this.resolvedFirstFactor === 'Password' &&
      !this.otpStep &&
      !this.twoFactorStep
    );
  }

  /**
   * Submit handler. When `useBetterAuth` is on, web/Electron/iOS/Android all use
   * Better Auth (`loginWebDispatch` / `loginIOS` / `loginAndroid`). When off,
   * Electron uses IPC MSAL and iOS/Android use MSAL redirect.
   */
  login(formValue: { email: string; password?: string }) {
    // In 2FA mode, pressing Enter submits the form; route it to code verification
    // rather than re-running the password sign-in.
    if (this.twoFactorStep) {
      void this.verifyTwoFactor(this.form.get('otp2fa')?.value);
      return;
    }
    // In OTP mode, pressing Enter in the code field submits this form; route it to
    // code verification rather than the password path (which would 401 on empty pw).
    if (this.otpStep) {
      void this.submitLoginCode(
        this.form.get('email')?.value,
        this.form.get('otp')?.value,
      );
      return;
    }

    // Email-first Continue: resolve method before password / email-OTP.
    if (
      this.useBetterAuth &&
      !this.usesNativeMsalFlow() &&
      !this.emailLookupDone &&
      !this.emailOtpEnabled
    ) {
      const emailCtrl = this.form.get('email');
      if (emailCtrl?.invalid) {
        emailCtrl.markAsTouched();
        return;
      }
      void this.resolveSignInMethodAndContinue(formValue.email);
      return;
    }

    // Surface field errors next to the inputs instead of a permanently disabled
    // Login button with no explanation.
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // Env kill-switch / resolved EmailOtp: request an email code.
    if (
      (this.emailOtpEnabled || this.resolvedFirstFactor === 'EmailOtp') &&
      !this.usesNativeMsalFlow()
    ) {
      void this.sendLoginCode(this.form.get('email')?.value);
      return;
    }
    if (!isOnline()) {
      this.form.get('email')?.setErrors({ isOffline: true });
      return;
    }
    this.incorrectCred = false;
    const platform = Capacitor.getPlatform();
    if (platform === platformType.ELECTRON && this.useBetterAuth) {
      void this.loginWebDispatch(formValue);
    } else if (platform === platformType.ELECTRON) {
      this.loginElectron(formValue.email);
    } else if (platform === platformType.IOS) {
      this.loginIOS(formValue);
    } else if (platform === platformType.ANDROID) {
      this.loginAndroid(formValue);
    } else {
      void this.loginWebDispatch(formValue);
    }
    this.submitted = true;
  }

  /**
   * Email-first probe against Profile v2. Unknown emails → Password (anti-enumeration).
   */
  private async resolveSignInMethodAndContinue(email: string) {
    if (!isOnline()) {
      this.form.get('email')?.setErrors({ isOffline: true });
      return;
    }
    this.submitted = true;
    try {
      const method = await firstValueFrom(
        this.profileV2Api.getSignInMethod(email),
      );
      const first = method.firstFactor === 'EmailOtp' ? 'EmailOtp' : 'Password';
      this.resolvedFirstFactor = first;
      this.resolvedRequiredMfaMethod = method.requiredMfaMethod;
      this.emailLookupDone = true;
      this.submitted = false;

      if (first === 'EmailOtp') {
        await this.sendLoginCode(email);
        return;
      }

      this.form.get('password')?.setValidators([Validators.required]);
      this.form.get('password')?.updateValueAndValidity();
    } catch {
      // Fail closed to password so login remains possible if Profile is down.
      this.resolvedFirstFactor = 'Password';
      this.resolvedRequiredMfaMethod = 'None';
      this.emailLookupDone = true;
      this.submitted = false;
      this.form.get('password')?.setValidators([Validators.required]);
      this.form.get('password')?.updateValueAndValidity();
    }
  }

  private async loginWebDispatch({
    email,
    password,
  }: {
    email: string;
    password?: string;
  }) {
    if (this.useBetterAuth) {
      await this.loginWebBetterAuth(email, password ?? '');
      return;
    }
    this.loginWebMsal(email);
  }

  /**
   * Better Auth web sign-in. Two entry points:
   *  - User landed at /auth/login fresh → POST creds, then start a new PKCE authorize flow.
   *  - Issuer redirected here with the authorize query intact → POST creds, then resume the
   *    existing authorize so the original PKCE verifier/state survives.
   */
  private async loginWebBetterAuth(email: string, password: string) {
    console.log('[auth] loginWebBetterAuth: POSTing sign-in/email', {
      email,
      platform: Capacitor.getPlatform(),
    });
    let res: BetterAuthSignInResponse | undefined;
    try {
      res = await firstValueFrom(
        this.http.post<BetterAuthSignInResponse>(
          `${environment.betterAuth.baseUrl}/api/auth/sign-in/email`,
          { email, password },
          { withCredentials: true },
        ),
      );
      console.log('[auth] sign-in/email succeeded', {
        hasToken: !!res?.token,
        twoFactorRedirect: !!res?.twoFactorRedirect,
      });
    } catch (err) {
      console.error('[auth] sign-in/email failed', err);
      this.incorrectCred = true;
      this.submitted = false;
      return;
    }

    // 2FA-enabled user: credentials verified but no session yet — enter the 2FA step.
    // Do NOT resume the OIDC authorize until the authenticator factor is verified.
    if (res?.twoFactorRedirect) {
      await this.enterTwoFactor(email);
      return;
    }

    this.resumeAfterSession(email);
  }

  /**
   * Credentials verified (password or email-OTP) but the account has 2FA enrolled, so
   * no session exists yet. Switch the single form into the 2FA verify step.
   * Defaults to authenticator-app TOTP when Profile `requiredMfaMethod` is
   * Authenticator (and `totpEnabled`); otherwise auto-sends the emailed/SMS OTP.
   */
  private async enterTwoFactor(email: string) {
    this.twoFactorEmail = email;
    this.otpStep = false;
    this.twoFactorStep = true;
    this.useBackupCode = false;
    this.submitted = false;
    this.incorrect2fa = false;

    if (this.totpEnabled && !this.emailLookupDone) {
      await this.ensureRequiredMfaMethod(email);
    }

    this.useAuthenticatorApp = this.preferAuthenticatorApp();
    if (!this.useAuthenticatorApp) {
      void this.send2faCode();
    }
  }

  /**
   * True when login 2FA should open on the authenticator-app path (verify-totp)
   * instead of auto-sending an emailed/SMS OTP.
   */
  private preferAuthenticatorApp(): boolean {
    if (!this.totpEnabled) {
      return false;
    }
    const method = this.resolvedRequiredMfaMethod;
    switch (method) {
      case 'Authenticator':
        return true;
      case 'None':
      case 'Email':
      case 'Sms':
        return false;
      default: {
        const _exhaustive: never = method;
        return _exhaustive;
      }
    }
  }

  /** Fill `resolvedRequiredMfaMethod` when email-first lookup was skipped. */
  private async ensureRequiredMfaMethod(email: string): Promise<void> {
    try {
      const method = await firstValueFrom(
        this.profileV2Api.getSignInMethod(email),
      );
      this.resolvedRequiredMfaMethod = method.requiredMfaMethod;
    } catch {
      this.resolvedRequiredMfaMethod = 'None';
    }
  }

  /** "Use an authenticator app instead" toggle. No send needed — TOTP codes are
   * generated offline by the app. Switching back to OTP requests a fresh code. */
  toggleAuthenticatorApp(): void {
    this.useAuthenticatorApp = !this.useAuthenticatorApp;
    this.useBackupCode = false;
    this.incorrect2fa = false;
    if (!this.useAuthenticatorApp) {
      void this.send2faCode();
    }
  }

  /** "Use a backup code instead" toggle — the escape hatch when neither the
   * emailed/SMS code nor the authenticator app is available. */
  toggleBackupCode(): void {
    this.useBackupCode = !this.useBackupCode;
    this.useAuthenticatorApp = false;
    this.incorrect2fa = false;
  }

  /** Requests (or resends) the 2FA OTP via the temporary 2FA cookie set at sign-in. */
  async send2faCode() {
    if (!isOnline()) {
      this.form.get('email')?.setErrors({ isOffline: true });
      return;
    }
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.betterAuth.baseUrl}/api/auth/two-factor/send-otp`,
          {},
          { withCredentials: true },
        ),
      );
    } catch {
      this.incorrect2fa = true;
    }
  }

  /**
   * Verifies the second factor, then resumes OIDC on success. Which endpoint is
   * hit depends on the toggled mode:
   *  - default / OTP       → `verify-otp`,         `{ code }`
   *  - useAuthenticatorApp → `verify-totp`,        `{ code }`
   *    (no `trustDevice` — authenticator is the primary challenge when Profile
   *    requires it, so we must not skip 2FA on the next login)
   *  - useBackupCode       → `verify-backup-code`, `{ code, trustDevice: true }`.
   */
  async verifyTwoFactor(code: string) {
    if (!isOnline()) {
      this.form.get('email')?.setErrors({ isOffline: true });
      return;
    }
    this.incorrect2fa = false;
    this.submitted = true;
    const path = this.useBackupCode
      ? 'verify-backup-code'
      : this.useAuthenticatorApp
      ? 'verify-totp'
      : 'verify-otp';
    const body = this.useBackupCode ? { code, trustDevice: true } : { code };
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.betterAuth.baseUrl}/api/auth/two-factor/${path}`,
          body,
          { withCredentials: true },
        ),
      );
    } catch {
      this.incorrect2fa = true;
      this.submitted = false;
      return;
    }
    this.completingSignIn = true;
    this.resumeAfterSession(this.twoFactorEmail);
  }

  /**
   * Shared post-sign-in step for both the password and email-OTP paths. Once a
   * Better Auth session cookie exists, either resume the in-flight OIDC authorize
   * request (preserving PKCE `code_challenge`/`state`/`nonce`) or start a fresh
   * code flow.
   */
  private resumeAfterSession(email: string) {
    // Password / email-OTP success (no 2FA) also goes through here — show the
    // same loading surface used after verifyTwoFactor.
    this.completingSignIn = true;
    console.log('[auth] resumeAfterSession', {
      pendingAuthorizeQuery: this.pendingAuthorizeQuery,
      hasOauthService: !!this.oauthService,
      loginUrl: this.oauthService?.loginUrl,
      discoveryDocumentLoaded: this.oauthService?.discoveryDocumentLoaded,
      returnUrl: this.returnUrl,
    });
    if (this.pendingAuthorizeQuery) {
      const resumeUrl = buildOidcAuthorizeResumeUrl(
        environment.betterAuth.baseUrl,
        this.pendingAuthorizeQuery,
        email,
      );
      console.log('[auth] resuming pending OIDC authorize', { resumeUrl });
      window.location.assign(resumeUrl);
      return;
    }

    if (!this.oauthService) {
      // Session cookie exists, but this shell never registered OAuthModule —
      // do not surface the password-failure copy for a missing OIDC client.
      console.error(
        '[auth] Better Auth session established but OAuthService is unavailable; cannot start OIDC code flow',
      );
      this.submitted = false;
      this.completingSignIn = false;
      this.profileLoadError = true;
      return;
    }
    if (this.oauthService.loginUrl === '') {
      // `initCodeFlow` silently waits for a `discovery_document_loaded` event
      // that will never fire again this session if discovery already failed
      // once at boot — this log is the tell for that stuck state.
      console.error(
        '[auth] OAuthService.loginUrl is empty — discovery document never loaded; initCodeFlow will hang waiting for discovery_document_loaded',
      );
    }
    console.log('[auth] calling oauthService.initCodeFlow', {
      loginUrl: this.oauthService.loginUrl,
      loginHint: email,
    });
    this.oauthService.initCodeFlow(encodeURIComponent(this.returnUrl), {
      login_hint: email,
    });
  }

  /** "Email me a code instead" — passwordless send. Only needs a valid email. */
  async sendLoginCode(email: string) {
    if (!isOnline()) {
      this.form.get('email')?.setErrors({ isOffline: true });
      return;
    }
    this.genericSendError = false;
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.betterAuth.baseUrl}/api/auth/email-otp/send-verification-otp`,
          { email, type: 'sign-in' },
          { withCredentials: true },
        ),
      );
      this.otpStep = true;
    } catch {
      // Only transport failures reach here — an unknown user returns 200 with no
      // email sent, so there is no enumeration signal to leak (analysis §1/§6).
      this.genericSendError = true;
    }
  }

  /** "Verify code" submit in OTP mode. Establishes the session, then resumes OIDC. */
  async submitLoginCode(email: string, otp: string) {
    if (!isOnline()) {
      this.form.get('email')?.setErrors({ isOffline: true });
      return;
    }
    this.incorrectCode = false;
    this.submitted = true;
    let res: BetterAuthSignInResponse | undefined;
    try {
      res = await firstValueFrom(
        this.http.post<BetterAuthSignInResponse>(
          `${environment.betterAuth.baseUrl}/api/auth/sign-in/email-otp`,
          { email, otp },
          { withCredentials: true },
        ),
      );
    } catch {
      this.incorrectCode = true;
      this.submitted = false;
      return;
    }
    // 2FA enrolled: the email code was the login factor — still require the second
    // factor before a session is granted. Depends on the server emitting
    // `twoFactorRedirect` from the email-OTP sign-in (see 2FA analysis §6).
    if (res?.twoFactorRedirect) {
      await this.enterTwoFactor(email);
      return;
    }
    this.resumeAfterSession(email);
  }

  /** "Back" link — return to email-first entry (or credentials before lookup). */
  backToCredentials() {
    this.otpStep = false;
    this.incorrectCode = false;
    this.genericSendError = false;
    this.form.get('otp')?.reset();
    // reset 2FA mode too
    this.twoFactorStep = false;
    this.incorrect2fa = false;
    this.useAuthenticatorApp = false;
    this.useBackupCode = false;
    this.form.get('otp2fa')?.reset();
    // Reset email-first lookup so Continue runs again.
    this.emailLookupDone = false;
    this.resolvedFirstFactor = null;
    this.resolvedRequiredMfaMethod = 'None';
    this.form.get('password')?.clearValidators();
    this.form.get('password')?.reset();
    this.form.get('password')?.updateValueAndValidity();
    this.submitted = false;
  }

  /**
   * It opens the developer tools if the devToolCount is greater than 9.
   */
  openDevTool() {
    this.devToolCount++;
    if (this.devToolCount > 9) {
      window.api?.send(IpcMessages.OPEN_DEV_TOOLS);
    }
  }

  /**
   * It sends a message to the main process to open the login window, and then waits for a response from
   * the main process
   * @param {string} [email] - The email address of the user to log in. If not provided, the user will be
   * prompted to enter their email address.
   */
  private loginElectron(email: string) {
    window.api?.send(IpcMessages.LOGIN, { email });
  }

  /**
   * iOS login. When `useBetterAuth` is on, opens the OIDC authorize URL in the
   * system browser (`loginNativeSystemBrowser`) — no credentials are collected
   * in-app. Otherwise falls back to MSAL redirect via Cordova InAppBrowser
   * (`CustomNavigationClient`).
   */
  private loginIOS(formValue: { email: string; password?: string }) {
    if (this.useBetterAuth) {
      this.loginNativeSystemBrowser();
      return;
    }
    if (!this.authService || !this.msalGuardConfig) {
      return;
    }
    const email = formValue.email;
    const navigationClient = new CustomNavigationClient();
    this.authService.instance.setNavigationClient(navigationClient);
    let authRequestConfig = {} as RedirectRequest;

    if (this.msalGuardConfig.authRequest) {
      authRequestConfig = {
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest;
    }

    authRequestConfig.loginHint = email;
    authRequestConfig.domainHint = email.split('@')[1];
    authRequestConfig.redirectStartPage = this.returnUrl || '/';

    this.initializeMsalThenRedirect(authRequestConfig);
  }

  /**
   * Android login. When `useBetterAuth` is on, opens the OIDC authorize URL in
   * the system browser (`loginNativeSystemBrowser`) — no credentials are
   * collected in-app. Otherwise falls back to MSAL redirect via Cordova
   * InAppBrowser (`CustomNavigationAndroidClient`).
   */
  private loginAndroid(formValue: { email: string; password?: string }) {
    if (this.useBetterAuth) {
      this.loginNativeSystemBrowser();
      return;
    }
    if (!this.authService || !this.msalGuardConfig) {
      return;
    }
    const email = formValue.email;
    const navigationClient = new CustomNavigationAndroidClient();
    this.authService.instance.setNavigationClient(navigationClient);
    let authRequestConfig = {} as RedirectRequest;

    if (this.msalGuardConfig.authRequest) {
      authRequestConfig = {
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest;
    }

    authRequestConfig.loginHint = email;
    authRequestConfig.domainHint = email.split('@')[1];
    authRequestConfig.redirectStartPage = this.returnUrl || '/';

    this.initializeMsalThenRedirect(authRequestConfig);
  }

  /**
   * Template entry point for the native "Continue to sign in" button
   * (`mobileNativeSystemBrowser`) — no form fields to collect, so this skips
   * `login()`'s submit pipeline entirely.
   */
  continueToSignIn() {
    this.loginNativeSystemBrowser();
  }

  /**
   * Opens the OIDC authorize URL in the system browser via the `openUri`
   * override configured in `escrowtab-oauth.config.ts`. The credential step
   * (login form, 2FA, etc.) renders on `webapp-*.escrowtab.com` itself; the app
   * regains control through the `escrowtab://auth/callback` deep link handled
   * in `AppComponent`.
   */
  private loginNativeSystemBrowser() {
    if (!this.oauthService) {
      console.error(
        '[auth] loginNativeSystemBrowser: OAuthService unavailable',
      );
      this.profileLoadError = true;
      return;
    }
    // `[disabled]="submitted"` only takes effect after the next change-detection
    // pass, so a double tap (or a duplicate touchstart/click pair some iOS
    // WebViews deliver) can invoke this twice before the button actually
    // disables — two concurrent `initCodeFlow` calls race on the same
    // `nonce`/`PKCE_verifier` localStorage keys, and whichever call's
    // `createAndSaveNonce()` resolves last silently wins, corrupting whichever
    // browser tab is left open. Bail out synchronously on re-entry.
    if (this.submitted) {
      return;
    }
    this.submitted = true;
    // Defend against a stale nonce/PKCE_verifier pair left behind by a prior
    // attempt that never completed (e.g. the app was killed while the system
    // browser was open) — a leftover value here would make this attempt's
    // callback fail nonce validation even though the new authorize request
    // itself is fine.
    try {
      localStorage.removeItem('nonce');
      localStorage.removeItem('PKCE_verifier');
    } catch {
      // localStorage unavailable (e.g. private mode) — initCodeFlow will
      // surface its own error if this matters.
    }
    this.oauthService.initCodeFlow(encodeURIComponent(this.returnUrl || '/'));
  }

  private isMobileNativePlatform(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === platformType.IOS || platform === platformType.ANDROID;
  }

  /** Legacy MSAL web flow. Preserved for `useBetterAuth=false`. */
  private loginWebMsal(email: string) {
    if (!this.authService || !this.msalGuardConfig) {
      return;
    }
    let authRequestConfig = {} as RedirectRequest;

    if (this.msalGuardConfig.authRequest) {
      authRequestConfig = {
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest;
    }

    authRequestConfig.loginHint = email;
    authRequestConfig.domainHint = email.split('@')[1];
    authRequestConfig.redirectStartPage = this.returnUrl || '/';

    this.initializeMsalThenRedirect(authRequestConfig);
  }

  /**
   * `MsalService.loginRedirect()` delegates straight to the underlying
   * `PublicClientApplication.loginRedirect()`, which throws
   * `uninitialized_public_client_application` unless `instance.initialize()`
   * has already resolved. `AppComponent.bootstrapMsal()` normally does this
   * at app startup, but only when this platform actually goes through the
   * MSAL path (see `usesNativeMsalFlow`) — always (re-)initialize here too
   * (idempotent/no-op if already initialized) so login never depends on
   * app-bootstrap timing.
   */
  private initializeMsalThenRedirect(authRequestConfig: RedirectRequest) {
    this.authService?.initialize().subscribe({
      next: () => this.authService?.loginRedirect(authRequestConfig),
      error: (err) => {
        this.incorrectCred = true;
        console.error('MSAL initialize failed before loginRedirect', err);
      },
    });
  }
  /**
   * check if the device is native
   *
   * @return {*}  {boolean}
   * @memberof LoginComponent
   */
  isNative(): boolean {
    const platform = Capacitor.getPlatform();
    return (
      platform === platformType.ELECTRON ||
      platform === platformType.IOS ||
      platform === platformType.ANDROID
    );
  }

  /**
   * True when this platform should still use the legacy MSAL flow.
   * iOS/Android/Electron/web all follow the shared `useBetterAuth` flag once
   * native shells route through `loginWebDispatch` (see `loginIOS` /
   * `loginAndroid`). Delegates to the shared helper.
   */
  private usesNativeMsalFlow(): boolean {
    return usesNativeMsalFlow(this.useBetterAuth);
  }

  /**
   * Better Auth email-first: start with email only. Password validators are added
   * after sign-in-method resolves to Password. OTP / 2FA controls are optional.
   */
  private createForm(): UntypedFormGroup {
    const controls: Record<string, [unknown, ...unknown[]]> = {
      email: [null, [Validators.required, Validators.email]],
    };
    if (this.useBetterAuth && !this.emailOtpEnabled) {
      // Present but not required until email-first lookup selects Password.
      controls['password'] = [null];
    }
    // OTP control for per-user EmailOtp or env kill-switch.
    if (this.useBetterAuth) {
      controls['otp'] = [null];
    }
    if (this.twoFactorEnabled) {
      controls['otp2fa'] = [null];
    }
    return this.fb.group(controls);
  }
}
